import type { LiveExtension, LiveQueueAgent } from '@arcnet/contracts';

/**
 * One state per queue agent, in the order a supervisor asks: are they even on, are they busy,
 * will they be free soon, can they take the next call (30 September 2026). The switch's status
 * and state answer different questions; this is the one answer the page shows.
 */
export type AgentTone = 'ok' | 'warn' | 'danger' | 'info' | 'muted';

export interface AgentState {
  label: string;
  tone: AgentTone;
  /** Seconds to show beside the label: time in the state, or wrap-up left. */
  secs: number | null;
  /** Would the queue offer them the next call. */
  free: boolean;
  /** For sorting: busiest first, logged out last. */
  rank: number;
}

export function agentState(a: LiveQueueAgent, now: number, ext: LiveExtension | undefined): AgentState {
  const since = (ms: number | null) => (ms === null ? null : Math.max(0, Math.floor((now - ms) / 1000)));
  if (a.status === 'Logged Out') return { label: 'Logged out', tone: 'muted', secs: null, free: false, rank: 7 };
  if (a.status === 'On Break') return { label: 'On a break', tone: 'warn', secs: since(a.since), free: false, rank: 6 };
  if (a.state === 'In a queue call') return { label: 'On a queue call', tone: 'info', secs: since(a.since), free: false, rank: 1 };
  if (a.state === 'Receiving') return { label: 'Ringing', tone: 'info', secs: null, free: false, rank: 2 };
  if (a.ready_at !== null && a.ready_at > now) {
    return { label: 'Wrap-up', tone: 'warn', secs: Math.ceil((a.ready_at - now) / 1000), free: false, rank: 3 };
  }
  if (ext?.state === 'on_call') return { label: 'On another call', tone: 'info', secs: since(ext.since), free: false, rank: 4 };
  return { label: 'Ready', tone: 'ok', secs: null, free: true, rank: 5 };
}
